import React, { useEffect, useState } from 'react';
import { Box, Button, MobileSlideDialog, DialogActions, DialogContent, DialogTitle, IconButton, CloseIcon } from '@/ui';
import DialogScreen from '../../engine/renderer/DialogScreen';
import vulnerabilityCircumstances from '../../engine/schema/onboarding/9-vulnerability-circumstances.json';
import yesVulnerabilityWho from '../../engine/schema/onboarding/10-yes-vulnerability.json';
import yesVulnerabilityCircumstancesSelection from '../../engine/schema/onboarding/11-yes-vulnerability.json';
import yesVulnerabilityConsent from '../../engine/schema/onboarding/12-yes-vulnerability.json';
import { useUI } from '@/shared/state/store';
import { OnboardingService } from '@/shared/api/services/onboardingService';
import { ProfileService } from '@/shared/api/services/profileService';
import { coerceConsentToHuman } from '@/shared/lib/utils/consent';

type ScreenSchema = Parameters<typeof DialogScreen>[0]['schema'];

interface VulnerabilityEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  userId: number;
  customerId: number;
  prefillStep7?: Record<string, unknown>;
  prefillStep8?: Record<string, unknown>;
  prefillStep9?: Record<string, unknown>;
  prefillStep10?: Record<string, unknown>;
}

const schemas: ScreenSchema[] = [
  vulnerabilityCircumstances as unknown as ScreenSchema, // gating question (yes/no)
  yesVulnerabilityWho as unknown as ScreenSchema,
  yesVulnerabilityCircumstancesSelection as unknown as ScreenSchema,
  yesVulnerabilityConsent as unknown as ScreenSchema,
];

const VulnerabilityEditDialog: React.FC<VulnerabilityEditDialogProps> = ({
  open,
  onClose,
  onSaved,
  userId: _userId,
  customerId,
  prefillStep7,
  prefillStep8,
  prefillStep9,
  prefillStep10,
}) => {
  const { setLoading, addAlert } = useUI();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [data7, setData7] = useState<Record<string, unknown>>(prefillStep7 || {});
  const [data8, setData8] = useState<Record<string, unknown>>(prefillStep8 || {});
  const [data9, setData9] = useState<Record<string, unknown>>(prefillStep9 || {});
  const [data10, setData10] = useState<Record<string, unknown>>(prefillStep10 || {});

  const mergeCustomerDataLocalStorage = (patch: Record<string, unknown>): void => {
    if (typeof window === 'undefined') return;
    try {
      const existingRaw = localStorage.getItem('customerData');
      const existing =
        existingRaw && existingRaw.trim().length > 0
          ? (JSON.parse(existingRaw) as Record<string, unknown>)
          : {};
      const merged = { ...existing, ...patch };
      // Preserve customerId for consumers that rely on it
      if (merged.customerId === undefined && (existing.customerId ?? existing.id) !== undefined) {
        merged.customerId = existing.customerId ?? existing.id;
      }
      localStorage.setItem('customerData', JSON.stringify(merged));
    } catch {
      // ignore storage errors
    }
  };

  const formIds = ['vuln-step-7', 'vuln-step-8', 'vuln-step-9', 'vuln-step-10'];
  const currentSchema = schemas[currentStep] as ScreenSchema;
  const currentInitialData = [data7, data8, data9, data10][currentStep] || {};
  const currentFormId = formIds[currentStep];

  useEffect(() => {
    if (open) {

      
      setCurrentStep(0);
      setData7(prefillStep7 || {});
      setData8(prefillStep8 || {});
      setData9(prefillStep9 || {});
      setData10(prefillStep10 || {});
      
    }
  }, [open, prefillStep7, prefillStep8, prefillStep9, prefillStep10]);

  const handleSubmitStep = async (data: Record<string, unknown>) => {
    // Step 0: gating (yes/no)
    if (currentStep === 0) {
      const merged7 = { ...data7, ...data };
      setData7(merged7);

      const answer = String(merged7?.hasVulnerabilityCircumstances || '').toLowerCase();
      if (answer !== 'yes') {
        // If user selects No (or skips), clear downstream steps and save immediately
        try {
          setIsSaving(true);
          setLoading('vulnerability-save', true);
          const clearedStep8 = { vulnerabilityCircumstancesAudience: 'None' };
          const clearedStep9 = { vulnerabilityCircumstancesDetails: [] as string[] };
          // Persist a definitive "No" so downstream consumers don't keep a stale "Yes".
          // Some environments ignore empty-string clears for this field.
          const clearedStep10 = { healthDataSharingConsent: 'N' };
          // Keep local dialog state in sync in case the user continues editing without closing
          setData8(clearedStep8);
          setData9(clearedStep9);
          setData10(clearedStep10);
          const payload = {
            userId: _userId,
            'onboarding-step-7': merged7, // Save gating question answer
            // IMPORTANT: include all vulnerability steps so PUT clears persisted data
            'onboarding-step-8': clearedStep8,
            'onboarding-step-9': clearedStep9,
            'onboarding-step-10': clearedStep10,
          } as Record<string, unknown>;
          await OnboardingService.updateVulnerability(payload, customerId as number, { skipErrorCapture: false });

          // Persist customer mapping as cleared/none
          const field2Value = String(merged7.hasVulnerabilityCircumstances || '').toLowerCase() === 'yes' ? 'Y' : 'N';
          const customerPatch = {
            field2TxtFld: field2Value, // Store gating question answer (Y/N)
            field3TxtFld: 'None', // audience explicitly set to None
            field4TxtFld: 'No'    // store explicit No so API doesn't retain stale Yes
          } as Record<string, unknown>;
          await ProfileService.patchCustomerData(customerId, customerPatch as any);
          // Keep localStorage snapshot in sync for dashboard query-param builders
          mergeCustomerDataLocalStorage(customerPatch);

          addAlert({ severity: 'success', message: 'Personal circumstances updated', autoHide: true, duration: 1500 });
          await onSaved();
          return;
        } catch {
          addAlert({ severity: 'error', message: 'Failed to save personal circumstances', autoHide: true, duration: 2000 });
        } finally {
          setIsSaving(false);
          setLoading('vulnerability-save', false);
        }
      } else {
        // Continue to next step when Yes
        setCurrentStep(1);
      }
      return;
    }

    // Step 1: audience
    if (currentStep === 1) {
      setData8((prev) => ({ ...prev, ...data }));
      setCurrentStep(2);
      return;
    }

    // Step 2: categories
    if (currentStep === 2) {
      setData9((prev) => ({ ...prev, ...data }));
      setCurrentStep(3);
      return;
    }

    // Step 3: consent (final save)
    if (currentStep === 3) {
      const merged8 = { ...data8 };
      const merged9 = { ...data9 };
      const merged10 = { ...data10, ...data };
      setData10(merged10);
      try {
        setIsSaving(true);
        setLoading('vulnerability-save', true);
        const payload = {
          userId: _userId,
          'onboarding-step-7': data7, // Save gating question answer
          'onboarding-step-8': merged8,
          'onboarding-step-9': merged9,
          'onboarding-step-10': merged10,
        } as Record<string, unknown>;
        await OnboardingService.updateVulnerability(payload, customerId as number, { skipErrorCapture: false });

        // Update customer data with field mappings
        const field2Value = String(data7.hasVulnerabilityCircumstances || '').toLowerCase() === 'yes' ? 'Y' : 'N';
        const rawConsent =
          typeof merged10.healthDataSharingConsent === 'string'
            ? merged10.healthDataSharingConsent
            : undefined;
        const consentValue = coerceConsentToHuman(rawConsent) ?? '';
        const apiPayload: Partial<{ field2TxtFld: unknown; field3TxtFld: unknown; field4TxtFld: unknown }> = {
          field2TxtFld: field2Value, // Store gating question answer (Y/N)
          field3TxtFld: merged8.vulnerabilityCircumstancesAudience,
          field4TxtFld: consentValue,
        };
        await ProfileService.patchCustomerData(customerId, apiPayload as any);
        mergeCustomerDataLocalStorage(apiPayload as Record<string, unknown>);

        addAlert({ severity: 'success', message: 'Personal circumstances updated', autoHide: true, duration: 1500 });
        await onSaved();
      } catch {
        addAlert({ severity: 'error', message: 'Failed to save personal circumstances', autoHide: true, duration: 2000 });
      } finally {
        setIsSaving(false);
        setLoading('vulnerability-save', false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
    else onClose();
  };

  return (
    <MobileSlideDialog open={open} onClose={onClose} fullWidth maxWidth="sm" keepMounted={false}>
      <DialogTitle data-testid="components-profile-vulnerability-edit-dialog-header-action" sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Edit personal circumstances</span>
        <IconButton data-testid="components-profile-vulnerability-edit-dialog-btn-on-close"
          onClick={onClose}
          sx={{ color: '#6b7280' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, overflow: 'auto' }}>
        <Box data-testid="components-profile-vulnerability-edit-dialog-container-action" sx={{ pt: 1 }}>
          {(() => {
    
            return null;
          })()}
          <DialogScreen
            key={`vulnerability-${currentStep}`}
            schema={currentSchema}
            hideHeader
            hideActions
            initialData={currentInitialData}
            onSubmit={(data) => handleSubmitStep(data as Record<string, unknown>)}
            formId={currentFormId || ''}
            onWarningAction={() => false}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e4e7eb' }}>
        <Button data-testid="components-profile-vulnerability-edit-dialog-btn-handle-back" variant="outlined" onClick={handleBack} sx={{ 
          flex: { xs: 1, md: 'initial' },
          minWidth: { xs: 'auto', md: '140px' },
          py: 1.5,
          borderRadius: 2,
          fontSize: '16px',
          fontWeight: 500,
          textTransform: 'none',
        }} disabled={isSaving}>
          Back
        </Button>
        <Button data-testid="components-profile-vulnerability-edit-dialog-btn-action" variant="contained" color="primary" type="submit" form={currentFormId} sx={{ 
          flex: { xs: 1, md: 'initial' },
          minWidth: { xs: 'auto', md: '140px' },
          py: 1.5,
          borderRadius: 2,
          fontSize: '16px',
          fontWeight: 500,
          textTransform: 'none',
          backgroundColor: '#003F44',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#003F44',
            boxShadow: 'none',
            border: '0.5px solid #003F44'
          },
          '&:disabled': {
            backgroundColor: '#E8EFEE',
            color: '#00243E',
          }
        }} disabled={isSaving}>
          {isSaving ? 'Saving...' : (currentStep === 2 ? 'Save' : 'Continue')}
        </Button>
      </DialogActions>
    </MobileSlideDialog>
  );
};

export default VulnerabilityEditDialog;




